import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ReportPieChart } from '@/components/reports/shared';
import {
  PieChart as PieChartIcon,
  Activity,
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface StatusDistributionProps {
  data: Array<{
    status: string;
    count: number;
    value: number;
    percentage: number;
  }>;
  loading?: boolean;
}

const statusConfig = {
  draft: {
    label: 'Rascunhos',
    color: 'hsl(var(--muted))',
    textColor: 'text-foreground',
    bgColor: 'bg-card',
    icon: FileText,
    description: 'Em edição',
  },
  sent: {
    label: 'Enviados',
    color: 'hsl(var(--chart-3))',
    textColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10',
    icon: Clock,
    description: 'Aguardando resposta',
  },
  approved: {
    label: 'Aprovados',
    color: 'hsl(var(--chart-2))',
    textColor: 'text-finance-green',
    bgColor: 'bg-green-500/10',
    icon: CheckCircle,
    description: 'Convertidos em vendas',
  },
  rejected: {
    label: 'Rejeitados',
    color: 'hsl(var(--chart-4))',
    textColor: 'text-finance-red',
    bgColor: 'bg-red-500/10',
    icon: XCircle,
    description: 'Não aprovados',
  },
  expired: {
    label: 'Expirados',
    color: 'hsl(var(--chart-5))',
    textColor: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-500/10',
    icon: AlertTriangle,
    description: 'Prazo expirado',
  },
};

export function StatusDistribution({ data, loading = false }: StatusDistributionProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-80" />
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare data for pie chart
  const pieChartData = data.map(status => ({
    name: statusConfig[status.status as keyof typeof statusConfig]?.label || status.status,
    value: status.count,
    color: statusConfig[status.status as keyof typeof statusConfig]?.color,
  }));

  // Calculate totals
  const totalCount = data.reduce((sum, status) => sum + status.count, 0);
  const totalValue = data.reduce((sum, status) => sum + status.value, 0);

  // Calculate conversion rates
  const sentBudgets = data.find(s => s.status === 'sent')?.count || 0;
  const approvedBudgets = data.find(s => s.status === 'approved')?.count || 0;
  const totalSentPlusApproved = sentBudgets + approvedBudgets;
  const approvalRate = totalSentPlusApproved > 0 ? (approvedBudgets / totalSentPlusApproved) * 100 : 0;

  const formatValue = (value: number) => formatCurrency(value);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {data.map((status) => {
          const config = statusConfig[status.status as keyof typeof statusConfig];
          const Icon = config?.icon || Activity;

          return (
            <Card key={status.status} className={cn('card-elevated', config?.bgColor)}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{config?.label}</CardTitle>
                <Icon className={cn('h-4 w-4', config?.textColor)} />
              </CardHeader>
              <CardContent>
                <div className={cn('text-2xl font-bold', config?.textColor)}>
                  {status.count}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatValue(status.value)} ({status.percentage.toFixed(1)}%)
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="h-5 w-5" />
            Distribuição por Status
          </CardTitle>
          <CardDescription>
            Análise detalhada dos orçamentos por status atual
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="flex flex-col items-center justify-center">
              <ReportPieChart
                data={pieChartData}
                height={300}
                showLegend={true}
                showPercentages={true}
                showLabels={true}
                innerRadius={60}
              />
            </div>

            {/* Status List */}
            <div className="space-y-4">
              {data.map((status) => {
                const config = statusConfig[status.status as keyof typeof statusConfig];
                const Icon = config?.icon || Activity;

                return (
                  <div key={status.status} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={cn('h-4 w-4', config?.textColor)} />
                        <span className="font-medium">{config?.label}</span>
                        <Badge variant="outline" className="text-xs">
                          {status.count}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{formatValue(status.value)}</div>
                        <div className="text-sm text-muted-foreground">
                          {status.percentage.toFixed(1)}% do total
                        </div>
                      </div>
                    </div>
                    <Progress value={status.percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {config?.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Análise de Conversão
            </CardTitle>
            <CardDescription>
              Taxas de conversão entre os estágios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-green-700 dark:text-green-200">Taxa de Aprovação</span>
                  <span className="text-2xl font-bold text-finance-green">
                    {approvalRate.toFixed(1)}%
                  </span>
                </div>
                <div className="text-sm text-green-600 dark:text-green-300">
                  {approvedBudgets} de {totalSentPlusApproved} orçamentos enviados foram aprovados
                </div>
                <Progress value={approvalRate} className="h-2 mt-2" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {sentBudgets}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Aguardando resposta
                  </div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-lg font-bold text-finance-green">
                    {approvedBudgets}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Aprovados
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Resumo Financeiro por Status
            </CardTitle>
            <CardDescription>
              Distribuição de valores por status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.map((status) => {
                const config = statusConfig[status.status as keyof typeof statusConfig];
                const valuePercentage = totalValue > 0 ? (status.value / totalValue) * 100 : 0;

                return (
                  <div key={status.status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'w-3 h-3 rounded-full',
                          config?.bgColor
                        )}
                      />
                      <span className="text-sm font-medium">{config?.label}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatValue(status.value)}</div>
                      <div className="text-xs text-muted-foreground">
                        {valuePercentage.toFixed(1)}% do valor total
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Insights do Funil</CardTitle>
          <CardDescription>
            Recomendações baseadas na distribuição atual
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Oportunidades Imediatas</h4>
              <p className="text-sm text-muted-foreground">
                {sentBudgets > 0
                  ? `${sentBudgets} orçamentos aguardando resposta. Considere fazer follow-up para aumentar as chances de aprovação.`
                  : 'Nenhum orçamento aguardando resposta no momento.'
                }
              </p>
            </div>

            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Ponto de Atenção</h4>
              <p className="text-sm text-muted-foreground">
                {data.find(s => s.status === 'draft')?.count > 5
                  ? 'Há muitos rascunhos pendentes. Considere finalizar e enviar esses orçamentos.'
                  : 'O número de rascunhos está controlado.'
                }
              </p>
            </div>

            <div className="p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Performance</h4>
              <p className="text-sm text-muted-foreground">
                {approvalRate >= 70
                  ? 'Excelente taxa de aprovação! Continue com a estratégia atual.'
                  : approvalRate >= 50
                  ? 'Taxa de aprovação razoável, mas há espaço para melhorias.'
                  : 'A taxa de aprovação precisa de atenção. Revise o processo de vendas.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}